import React, { useEffect, useState } from "react";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/UserRoleSlice";
import AddUserRoleWrapper from "../Add/AddUserRoleWrapper";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import EditUserRoleWrapper from "../Edit/EditUserRoleWrapper";
import {
  useChangeStatusUserRoleByIdMutation,
  useDeleteUserRoleByIdMutation,
  useGetAllUserRoleDataQuery,
} from "src/services/UserRoleService";
import UserRolesListing from "./UserRoleListing";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const UserRoleListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    isOpenAddDialog,
    isOpenEditDialog,
    searchValue,
    isTableLoading,
  } = useSelector((state: RootState) => state.userRole);
  const [selectedUserRoleId, setSelectedUserRoleId] = useState<string>();
  const [deleteUserRole] = useDeleteUserRoleByIdMutation();
  const [changeStatusUserRole] = useChangeStatusUserRoleByIdMutation();
  const { data, isLoading, isFetching } = useGetAllUserRoleDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["roleName"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleDeleteUserRole = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this User Role ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteUserRole(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusUserRole = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this User Role ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusUserRole({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };
  const canEdit = usePermission("USER_ROLE", "UPDATE");
  const canDelete = usePermission("USER_ROLE", "DELETE");
  const canActive = usePermission("USER_ROLE", "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    { field: "roleName", headerName: "Role Name", flex: "flex-[1_1_0%]" },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "end",
      extraClasses: "",
      renderCell: (row: any) => {
        const menuOptions = [];

        if (canEdit) {
          menuOptions.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedUserRoleId(row?._id);
            },
          });
        }

        if (canActive) {
          menuOptions.push({
            label: "Active/Deactive",
            onClick: () => handleChangeStatusUserRole(row?._id),
          });
        }

        if (canDelete) {
          menuOptions.push({
            label: "Delete",
            onClick: () => handleDeleteUserRole(row?._id),
          });
        }

        return <ATMMenu options={menuOptions} />;
      },
    },
  ];

  return (
    <>
      <div>
        <UserRolesListing
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddUserRoleWrapper
          onClose={() => dispatch(setIsOpenAddDialog(false))}
        />
      ) : null}
      {isOpenEditDialog ? (
        <EditUserRoleWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedUserRoleId={selectedUserRoleId || ""}
        />
      ) : null}
    </>
  );
};

export default UserRoleListingWrapper;
